<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import DealsAPI from 'dashboard/api/deals';
import DealStagesAPI from 'dashboard/api/dealStages';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import DealDialog from '../components/DealDialog.vue';

const { t } = useI18n();
const store = useStore();

const columns = ref([]);
const isLoading = ref(false);
const statusFilter = ref('');
const assigneeFilter = ref('');
const selectedDeal = ref(null);
const selectedStage = ref(null);
const isSavingStage = ref(false);
const isDeletingStage = ref(false);
const stageForm = ref({ name: '', color: '#57DDE0' });
const dealDialog = ref(null);
const stageDialog = ref(null);

const stageColors = [
  { value: '#57DDE0', className: 'bg-woot-400' },
  { value: '#4ADE80', className: 'bg-green-400' },
  { value: '#FACC15', className: 'bg-yellow-400' },
  { value: '#FB7185', className: 'bg-red-400' },
  { value: '#A78BFA', className: 'bg-violet-400' },
  { value: '#94A3B8', className: 'bg-slate-400' },
];

const verifiedAgents = computed(
  () => store.getters['agents/getVerifiedAgents']
);
const stages = computed(() =>
  columns.value.map(({ deals: _deals, ...stage }) => stage)
);
const stageDialogTitle = computed(() =>
  selectedStage.value
    ? t('DEALS.STAGE.EDIT_TITLE')
    : t('DEALS.STAGE.CREATE_TITLE')
);
const dealFilters = computed(() => ({
  ...(statusFilter.value ? { status: statusFilter.value } : {}),
  ...(assigneeFilter.value ? { assignee_id: assigneeFilter.value } : {}),
}));

const buildColumns = (stageRecords, deals) => {
  columns.value = stageRecords
    .slice()
    .sort((a, b) => a.position - b.position || a.id - b.id)
    .map(stage => ({
      ...stage,
      deals: deals
        .filter(deal => deal.deal_stage_id === stage.id)
        .sort((a, b) => a.position - b.position || a.id - b.id),
    }));
};

const loadBoard = async () => {
  isLoading.value = true;
  try {
    const [stageResponse, dealResponse] = await Promise.all([
      DealStagesAPI.get(),
      DealsAPI.get(dealFilters.value),
    ]);
    buildColumns(stageResponse.data, dealResponse.data);
  } catch {
    useAlert(t('DEALS.COMMON.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const reloadDeals = async () => {
  try {
    const { data } = await DealsAPI.get(dealFilters.value);
    buildColumns(stages.value, data);
  } catch {
    useAlert(t('DEALS.COMMON.LOAD_ERROR'));
  }
};

onMounted(async () => {
  await Promise.all([loadBoard(), store.dispatch('agents/get')]);
});

watch([statusFilter, assigneeFilter], reloadDeals);

const openStageDialog = async (stage = null) => {
  selectedStage.value = stage;
  stageForm.value = {
    name: stage?.name || '',
    color: stage?.color || '#57DDE0',
  };
  await nextTick();
  stageDialog.value.open();
};

const saveStage = async () => {
  if (!stageForm.value.name.trim()) return;

  isSavingStage.value = true;
  const payload = {
    deal_stage: {
      name: stageForm.value.name.trim(),
      color: stageForm.value.color,
    },
  };
  try {
    if (selectedStage.value) {
      await DealStagesAPI.update(selectedStage.value.id, payload);
      useAlert(t('DEALS.STAGE.UPDATE_SUCCESS'));
    } else {
      await DealStagesAPI.create(payload);
      useAlert(t('DEALS.STAGE.CREATE_SUCCESS'));
    }
    stageDialog.value.close();
    await loadBoard();
  } catch {
    useAlert(t('DEALS.STAGE.SAVE_ERROR'));
  } finally {
    isSavingStage.value = false;
  }
};

const deleteStage = async () => {
  // eslint-disable-next-line no-alert
  const isConfirmed = window.confirm(
    t('DEALS.STAGE.DELETE_CONFIRM', { name: selectedStage.value.name })
  );
  if (!isConfirmed) return;

  isDeletingStage.value = true;
  try {
    await DealStagesAPI.delete(selectedStage.value.id);
    useAlert(t('DEALS.STAGE.DELETE_SUCCESS'));
    stageDialog.value.close();
    await loadBoard();
  } catch {
    useAlert(t('DEALS.STAGE.DELETE_ERROR'));
    await loadBoard();
  } finally {
    isDeletingStage.value = false;
  }
};

const reorderStages = async () => {
  try {
    await DealStagesAPI.reorder(columns.value.map(stage => stage.id));
  } catch {
    useAlert(t('DEALS.STAGE.REORDER_ERROR'));
    await loadBoard();
  }
};

const handleDealChange = async (stage, event) => {
  const change = event.added || event.moved;
  if (!change) return;

  stage.deals.forEach((deal, index) => {
    deal.deal_stage_id = stage.id;
    deal.position = index;
  });

  try {
    await DealsAPI.move(change.element.id, {
      deal_stage_id: stage.id,
      position: change.newIndex,
    });
  } catch {
    useAlert(t('DEALS.COMMON.MOVE_ERROR'));
    await loadBoard();
  }
};

const openNewDeal = async () => {
  selectedDeal.value = null;
  await nextTick();
  dealDialog.value.open();
};

const openDeal = async deal => {
  try {
    const { data } = await DealsAPI.show(deal.id);
    selectedDeal.value = data;
    await nextTick();
    dealDialog.value.open();
  } catch {
    useAlert(t('DEALS.COMMON.LOAD_ERROR'));
  }
};

const handleDealSaved = async () => {
  selectedDeal.value = null;
  await loadBoard();
};

const stageColorClass = color =>
  stageColors.find(item => item.value === color)?.className || 'bg-slate-400';

const statusClass = status =>
  ({
    won: 'bg-n-teal-3 text-n-teal-11',
    lost: 'bg-n-ruby-3 text-n-ruby-11',
  })[status];

const statusLabel = status =>
  ({
    won: t('DEALS.STATUS.WON'),
    lost: t('DEALS.STATUS.LOST'),
  })[status];

const formatCurrency = deal =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: deal.currency,
  }).format(Number(deal.value));

const formatDate = value =>
  new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(
    new Date(`${value}T00:00:00`)
  );
</script>

<template>
  <main class="flex h-full min-h-0 flex-col bg-n-background text-n-slate-12">
    <header
      class="flex flex-col gap-4 border-b border-n-weak px-6 py-5 lg:flex-row lg:items-center lg:justify-between"
    >
      <div>
        <h1 class="mb-1 text-2xl font-medium">{{ t('DEALS.TITLE') }}</h1>
        <p class="mb-0 text-sm text-n-slate-10">{{ t('DEALS.DESCRIPTION') }}</p>
      </div>
      <div class="flex flex-wrap items-end gap-3">
        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
          {{ t('DEALS.FILTERS.STATUS') }}
          <select
            v-model="statusFilter"
            class="h-9 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          >
            <option value="">{{ t('DEALS.FILTERS.ALL_STATUSES') }}</option>
            <option value="open">{{ t('DEALS.STATUS.OPEN') }}</option>
            <option value="won">{{ t('DEALS.STATUS.WON') }}</option>
            <option value="lost">{{ t('DEALS.STATUS.LOST') }}</option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-xs font-medium text-n-slate-11">
          {{ t('DEALS.FILTERS.ASSIGNEE') }}
          <select
            v-model="assigneeFilter"
            class="h-9 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          >
            <option value="">{{ t('DEALS.FILTERS.ALL_ASSIGNEES') }}</option>
            <option
              v-for="agent in verifiedAgents"
              :key="agent.id"
              :value="agent.id"
            >
              {{ agent.available_name || agent.name }}
            </option>
          </select>
        </label>
        <Button
          color="slate"
          variant="faded"
          icon="i-lucide-columns-3"
          :label="t('DEALS.ACTIONS.ADD_STAGE')"
          @click="openStageDialog()"
        />
        <Button
          icon="i-lucide-plus"
          :label="t('DEALS.ACTIONS.NEW_DEAL')"
          :disabled="!columns.length"
          @click="openNewDeal"
        />
      </div>
    </header>

    <div
      v-if="isLoading"
      class="grid flex-1 place-content-center text-n-slate-10"
    >
      <div class="flex items-center gap-2 text-sm">
        <span class="i-lucide-loader-circle size-5 animate-spin" />
        {{ t('DEALS.COMMON.LOADING') }}
      </div>
    </div>

    <section
      v-else-if="!columns.length"
      class="grid flex-1 place-content-center px-6 text-center"
    >
      <div class="flex max-w-md flex-col items-center gap-3">
        <span class="i-lucide-columns-3 size-12 text-n-slate-9" />
        <h2 class="mb-0 text-lg font-medium">{{ t('DEALS.EMPTY.TITLE') }}</h2>
        <p class="mb-1 text-sm text-n-slate-10">
          {{ t('DEALS.EMPTY.DESCRIPTION') }}
        </p>
        <Button
          :label="t('DEALS.ACTIONS.ADD_STAGE')"
          @click="openStageDialog()"
        />
      </div>
    </section>

    <div v-else class="flex-1 overflow-x-auto overflow-y-hidden p-5">
      <Draggable
        v-model="columns"
        item-key="id"
        handle=".stage-drag-handle"
        class="flex h-full min-w-max gap-4"
        @end="reorderStages"
      >
        <template #item="{ element: stage }">
          <section
            class="flex h-full w-80 min-w-[320px] flex-col rounded-xl bg-n-alpha-2 outline outline-1 outline-n-weak"
          >
            <header
              class="flex items-center gap-2 border-b border-n-weak px-3 py-3"
            >
              <button
                type="button"
                class="stage-drag-handle cursor-grab text-n-slate-9 hover:text-n-slate-12"
                :aria-label="t('DEALS.STAGE.DRAG_HANDLE')"
              >
                <span class="i-lucide-grip-vertical block size-4" />
              </button>
              <span
                class="size-2.5 flex-shrink-0 rounded-full"
                :class="stageColorClass(stage.color)"
              />
              <h2 class="mb-0 min-w-0 flex-1 truncate text-sm font-medium">
                {{ stage.name }}
              </h2>
              <span class="text-xs text-n-slate-10">
                {{ t('DEALS.STAGE.DEALS_COUNT', stage.deals.length) }}
              </span>
              <Button
                type="button"
                icon="i-lucide-pencil"
                color="slate"
                variant="ghost"
                size="xs"
                :aria-label="t('DEALS.ACTIONS.EDIT_STAGE')"
                @click="openStageDialog(stage)"
              />
            </header>

            <Draggable
              v-model="stage.deals"
              item-key="id"
              group="deals"
              class="flex min-h-24 flex-1 flex-col gap-3 overflow-y-auto p-3"
              @change="handleDealChange(stage, $event)"
            >
              <template #item="{ element: deal }">
                <article
                  role="button"
                  tabindex="0"
                  class="cursor-pointer rounded-xl bg-n-background p-4 shadow-sm outline outline-1 outline-n-weak transition hover:outline-n-strong focus:outline-n-brand"
                  :aria-label="t('DEALS.CARD.OPEN_DETAILS')"
                  @click="openDeal(deal)"
                  @keydown.enter="openDeal(deal)"
                >
                  <div class="mb-3 flex items-start justify-between gap-2">
                    <h3 class="mb-0 text-sm font-medium text-n-slate-12">
                      {{ deal.title }}
                    </h3>
                    <span
                      v-if="deal.status !== 'open'"
                      class="rounded px-2 py-0.5 text-xs font-medium"
                      :class="statusClass(deal.status)"
                    >
                      {{ statusLabel(deal.status) }}
                    </span>
                  </div>
                  <p class="mb-0 text-sm text-n-slate-11">
                    {{ deal.contact.name }}
                  </p>
                  <p class="mb-3 text-xs text-n-slate-9">
                    {{
                      deal.contact.company_name || t('DEALS.CARD.NO_COMPANY')
                    }}
                  </p>
                  <p class="mb-3 text-base font-medium text-n-slate-12">
                    {{ formatCurrency(deal) }}
                  </p>
                  <div class="flex flex-col gap-1 text-xs text-n-slate-10">
                    <span>
                      {{
                        t('DEALS.CARD.STARTED_ON', {
                          date: formatDate(deal.started_on),
                        })
                      }}
                    </span>
                    <span>
                      {{
                        deal.valid_until
                          ? t('DEALS.CARD.VALID_UNTIL', {
                              date: formatDate(deal.valid_until),
                            })
                          : t('DEALS.CARD.NO_END_DATE')
                      }}
                    </span>
                  </div>
                  <div
                    class="mt-3 border-t border-n-weak pt-3"
                    @click.stop
                    @keydown.stop
                    @mousedown.stop
                    @touchstart.stop
                  >
                    <ComposeConversation
                      :contact-id="String(deal.contact.id)"
                      :contact="deal.contact"
                      align="start"
                    >
                      <template #trigger>
                        <Button
                          type="button"
                          icon="i-lucide-message-circle"
                          color="slate"
                          variant="ghost"
                          size="xs"
                          :label="t('DEALS.CARD.CONTACT')"
                        />
                      </template>
                    </ComposeConversation>
                  </div>
                </article>
              </template>
              <template #footer>
                <p
                  v-if="!stage.deals.length"
                  class="mb-0 py-6 text-center text-xs text-n-slate-9"
                >
                  {{ t('DEALS.EMPTY.NO_DEALS') }}
                </p>
              </template>
            </Draggable>
          </section>
        </template>
      </Draggable>
    </div>

    <Dialog
      ref="stageDialog"
      width="md"
      :title="stageDialogTitle"
      :show-cancel-button="false"
      :show-confirm-button="false"
    >
      <div class="flex flex-col gap-5">
        <Input
          v-model="stageForm.name"
          :label="t('DEALS.STAGE.NAME')"
          :placeholder="t('DEALS.STAGE.NAME_PLACEHOLDER')"
          required
        />
        <fieldset class="flex flex-col gap-2">
          <legend class="mb-1 text-heading-3 text-n-slate-12">
            {{ t('DEALS.STAGE.COLOR') }}
          </legend>
          <div class="flex gap-3">
            <button
              v-for="color in stageColors"
              :key="color.value"
              type="button"
              class="size-7 rounded-full outline-offset-2"
              :class="[
                color.className,
                stageForm.color === color.value
                  ? 'outline outline-2 outline-n-brand'
                  : '',
              ]"
              :aria-label="color.value"
              @click="stageForm.color = color.value"
            />
          </div>
        </fieldset>
      </div>
      <template #footer>
        <div class="flex w-full items-center justify-between gap-3">
          <Button
            v-if="selectedStage"
            color="ruby"
            variant="ghost"
            :label="t('DEALS.ACTIONS.DELETE')"
            :is-loading="isDeletingStage"
            @click="deleteStage"
          />
          <span v-else />
          <div class="flex gap-3">
            <Button
              color="slate"
              variant="faded"
              :label="t('DEALS.ACTIONS.CANCEL')"
              @click="stageDialog.close()"
            />
            <Button
              :label="t('DEALS.ACTIONS.SAVE')"
              :disabled="!stageForm.name.trim()"
              :is-loading="isSavingStage"
              @click="saveStage"
            />
          </div>
        </div>
      </template>
    </Dialog>

    <DealDialog
      ref="dealDialog"
      :deal="selectedDeal"
      :stages="stages"
      :agents="verifiedAgents"
      @close="selectedDeal = null"
      @saved="handleDealSaved"
      @deleted="handleDealSaved"
    />
  </main>
</template>
